import type { RelatedTemplate } from "AdminPanel/Spine/store/configStore";
import type { StaticStackItem } from "AdminPanel/Spine/lib/staticLayerStack";
import {
  buildStaticLayerRows,
  zIndexForAvatarTemplateBg,
  zIndexForAvatarTemplateCharacterKonva,
  zIndexForStaticLayer,
} from "AdminPanel/Spine/lib/staticLayerStack";
import { useEditorStore } from "CanvasCore/uiSlice";
import { useEditorStore as useSpineEditorStore } from "AdminPanel/Spine/store/editorStore";

/** 叠在主模版人物 Konva 之上（仅前景类） */
const AVATAR_TYPES_ABOVE_CHAR = new Set(["fgStatic", "fgEffect", "fg"]);

/** 头像编辑器：关联静态层 z-index（bg/bgEffect 在主模版人物后面；fg 在前面） */
export function zIndexForAvatarRelatedStatic(
  templateType: string | undefined,
  playIndex: number,
  relatedStackIndex: number,
): number {
  const z = zIndexForStaticLayer(templateType, playIndex, relatedStackIndex);
  const charZ = zIndexForAvatarTemplateCharacterKonva();
  const bgZ = zIndexForAvatarTemplateBg();
  if (templateType && AVATAR_TYPES_ABOVE_CHAR.has(templateType)) {
    return Math.max(z, charZ + 1);
  }
  return Math.max(bgZ + 1, Math.min(z, charZ - 1));
}

export function buildAvatarMergedStaticStackItems(args: {
  relatedStatic: {
    id: string;
    type?: string;
    config: { selection?: any[]; skus?: Record<string, any> };
  }[];
  selectedSkins: string[];
  relatedTemplateOrderIndexById?: Map<string, number>;
}): StaticStackItem[] {
  const items: StaticStackItem[] = [];
  const { relatedStatic, selectedSkins, relatedTemplateOrderIndexById } = args;

  for (const rel of relatedStatic) {
    const stackIdx = relatedTemplateOrderIndexById?.get(rel.id) ?? 0;
    for (const row of buildStaticLayerRows(rel.config, selectedSkins)) {
      items.push({
        kind: "img",
        key: `${rel.id}-${row.key}`,
        url: row.url,
        zIndex: zIndexForAvatarRelatedStatic(rel.type, row.playIndex, stackIdx),
      });
    }
  }

  return items;
}

export function splitRelatedTemplatesByCharacterLayer(
  related: RelatedTemplate[],
): {
  belowCharacter: RelatedTemplate[];
  aboveCharacter: RelatedTemplate[];
} {
  const belowCharacter: RelatedTemplate[] = [];
  const aboveCharacter: RelatedTemplate[] = [];
  for (const rel of related) {
    const type = rel.type;
    if (type && AVATAR_TYPES_ABOVE_CHAR.has(type)) {
      aboveCharacter.push(rel);
    } else {
      belowCharacter.push(rel);
    }
  }
  return { belowCharacter, aboveCharacter };
}

export function collectRelatedTemplateSkinNames(
  loaded: RelatedTemplate[] | undefined,
): Set<string> {
  const names = new Set<string>();
  for (const rel of loaded || []) {
    const skus = rel.config?.skus || {};
    for (const sku of Object.values(skus)) {
      const skinName = (sku as { skinName?: string })?.skinName;
      if (skinName) names.add(skinName);
    }
  }
  return names;
}

export function shouldUseAvatarSpinePreview(
  selectedSkins: string[],
  loadedRelatedTemplates: RelatedTemplate[] | undefined,
): boolean {
  if (!loadedRelatedTemplates?.length) return false;
  const related = collectRelatedTemplateSkinNames(loadedRelatedTemplates);
  return selectedSkins.some((s) => related.has(s));
}

export function resetAvatarEditorPreview(tid: string) {
  useEditorStore.getState().resetCanvas(tid);
  useSpineEditorStore.getState().resetPreview();
}
